<template>
  <v-card>
    <v-card-title>Add New Asset</v-card-title>
    <v-card-text>
      <v-stepper v-model="newAssetStep" class="elevation-0">
        <v-stepper-header>
          <template>
            <v-stepper-step :complete="newAssetStep > 1" step="1">
              Basic Info
            </v-stepper-step>
            <v-divider></v-divider>
            <v-stepper-step :complete="newAssetStep > 2" step="2">
              More Info
            </v-stepper-step>
            <v-divider></v-divider>
            <v-stepper-step :complete="newAssetStep > 3" step="3">
              Asset Created
            </v-stepper-step>
          </template>
        </v-stepper-header>
        <v-stepper-items>
          <v-stepper-content step="1">
            <v-container>
              <v-form v-model="valid" ref="basicInfoForm">
                <v-row v-if="createReason == 'Moving Existing Equipment to New Location'">
                  <v-alert text type="warning">
                    <span>Please email <a :href="accountManagerEmailLink" target="_blank">{{accountManagerEmail}}</a> to request your account manager to move the piece of equipment in order to maintain service and cost history. Please include the piece equipment, where it is currently located and where you are moving it.</span>
                  </v-alert>
                </v-row>
                <v-row>
                  <v-col cols="12" sm="6">
                    <EsSelect
                      :items="availableCreateReasons"
                      :customRule="rules.createReason"
                      v-model="createReason"
                      label="Reason for Adding New Equipment"
                      hide-details
                    />
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="12" sm="6">
                    <EsSelect
                      :items="locations"
                      :required="true"
                      itemText="name"
                      itemValue="id"
                      v-model="location"
                      label="Location"
                      hide-details
                    />
                  </v-col>
                  <v-col cols="12" sm="6">
                    <EsSelect
                      :items="serviceCategories"
                      required
                      v-model="serviceCategory"
                      itemText="name"
                      itemValue="id"
                      label="Service Category"
                      hide-details
                    />
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="12" sm="6">
                    <EsAutocomplete
                      :items="filteredAssetTypes"
                      v-model="assetType"
                      required
                      itemText="name"
                      returnObject
                      label="Asset Type"
                      @input="selectAssetType"
                      hide-details
                    />
                  </v-col>
                  <v-col cols="12" sm="6">
                    <EsTextField
                      v-model="description"
                      required
                      label="Description"
                      hide-details
                    />
                  </v-col>
                </v-row>
                <v-row>
                  <v-spacer></v-spacer>
                  <v-col>
                    <EsButtonCancel
                      block
                      label="Cancel"
                      @click="close()"
                    />
                  </v-col>
                  <v-col>
                    <EsButtonSave
                      block
                      label="Next"
                      :disabled="!valid"
                      @click="newAssetStep++"
                    />
                  </v-col>
                </v-row>
              </v-form>
            </v-container>
          </v-stepper-content>
          <v-stepper-content step="2">
            <v-container>
              <v-row>
                <v-col>
                  <v-alert text type="warning" v-if="missingInfo && !isSsp">
                    <span>Complete asset task will be created. Missing: {{missingInfo}}</span>
                  </v-alert>
                </v-col>
              </v-row>
              <v-row>
                <v-col :cols="$vuetify.breakpoint.smAndDown ? `12` : undefined">
                  <v-row>
                    <v-col class="pt-0">
                      <span class="text--disabled">More info</span>
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col>
                      <EsAutocompletePaginated
                        label="Manufacturer"
                        :getItems="getManufacturers"
                        :getItemById="getManufacturerById"
                        hide-details
                        itemValue="id"
                        itemText="name"
                        returnObject
                        :value="manufacturer"
                        :canAddNew="!isSsp"
                        @input="setManufacturer($event)"
                        :createNewItem="createNewManufacturer"
                      >
                        <template v-if="!isSsp" v-slot:new-item="{ onCancel, onSave }">
                          <EsAddNewItem
                            :searchItem="searchForNewManufacturer"
                            @cancel="onCancel"
                            @save="onSave({ name: $event })"/>
                        </template>
                      </EsAutocompletePaginated>
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col>
                      <v-combobox
                        outlined
                        label="Model #"
                        clearable
                        :items="suggestedModelNumbers"
                        v-model="modelNumber"
                        hide-details
                        @input="modelNumber = $event"
                        @keyup="modelNumber = $event.srcElement._value"
                      />
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col>
                      <v-combobox
                        outlined
                        label="Serial #"
                        clearable
                        :items="options"
                        v-model="serialNumber"
                        hide-details
                        @input="serialNumber = $event"
                        @keyup="serialNumber = $event.srcElement._value"
                      />
                    </v-col>
                  </v-row>
                </v-col>
                <v-col
                  class="ml-3"
                  :cols="$vuetify.breakpoint.smAndDown ? `12` : undefined">
                  <v-row style="padding-top: 5px;">
                    <span class="text--disabled">Upload photos</span>
                  </v-row>
                  <v-row>
                    <EsAssetPhotoUploader
                      :photos="photos"
                      :uploadHandler="fileUpload"
                      :getSignedUrl="getSignedUrl" />
                  </v-row>
                </v-col>
              </v-row>
              <v-row>
                <v-spacer></v-spacer>
                <v-col>
                  <EsButtonCancel
                    block
                    @click="close()"
                  />
                </v-col>
                <v-col>
                  <EsButtonSave
                    block
                    :loading="creatingAsset"
                    @click="createAsset()"
                  />
                </v-col>
              </v-row>
            </v-container>
          </v-stepper-content>
          <v-stepper-content step="3">
            <v-container>
              <v-row>
                <p>The asset was successfully created. Where to next?</p>
              </v-row>
              <v-row>
                <v-spacer></v-spacer>
                <v-col>
                  <EsButtonCancel
                    block
                    label="Close Overlay"
                    @click="close()"
                  />
                </v-col>
                <v-col>
                  <EsButtonAction block label="Add Another" @click="resetComponent()" />
                </v-col>
                <v-col v-if="showSelector">
                  <EsButtonSave block label="Select"
                    @click="$emit('assetSelected')" />
                </v-col>
              </v-row>
            </v-container>
          </v-stepper-content>
        </v-stepper-items>
      </v-stepper>
    </v-card-text>
  </v-card>
</template>

<script>
import {
  EsSelect,
  EsAutocomplete,
  EsTextField,
  EsButtonCancel,
  EsButtonSave,
  EsButtonAction,
} from '@86-repairs/design-system';
import EsAssetPhotoUploader from './EsAssetPhotoUploader.vue';
import EsAutocompletePaginated from '../EsAutocompletePaginated.vue';
import EsAddNewItem from '../EsAddNewItem.vue';

function initialData() {
  return {
    newAssetStep: 1,
    assetType: null,
    description: null,
    manufacturer: null,
    modelNumber: null,
    photos: [],
    location: null,
    serialNumber: null,
    valid: false,
    creatingAsset: false,
    options: ['N/A', 'Illegible', 'Missing'],
    predictedOptions: [],
    serviceCategory: null,
    createReason: null,
    rules: {
      createReason: value => !!value && value !== 'Moving Existing Equipment to New Location'
    }
  };
}

export default {
  name: 'EsAssetAdd',
  props: {
    serviceCategories: {
      type: Array,
      default: () => [],
    },
    getManufacturers: {
      type: Function,
      required: true,
    },
    getManufacturerById: {
      type: Function,
      required: true,
    },
    locations: {
      type: Array,
      default: () => [],
    },
    assetTypes: {
      type: Array,
      default: () => [],
    },
    selectedLocationId: {
      type: String,
    },
    showSelector: {
      type: Boolean,
      default: false,
    },
    getSignedUrl: {
      type: Function,
      required: false,
    },
    searchForNewManufacturer: {
      type: Function,
      required: false,
    },
    createNewManufacturer: {
      type: Function,
      required: false,
    },
    getAssetModelNumbers: {
      type: Function,
      required: true,
    },
    isSsp: {
      type: Boolean,
      default: false,
    },
    accountManagerEmail: {
      type: String,
      required: false,
    },
  },
  components: {
    EsSelect,
    EsAutocomplete,
    EsTextField,
    EsButtonCancel,
    EsButtonSave,
    EsButtonAction,
    EsAssetPhotoUploader,
    EsAutocompletePaginated,
    EsAddNewItem,
  },
  data: () => initialData(),
  computed: {
    types() {
      return this.assetTypes;
    },
    filteredAssetTypes() {
      return this.types.filter((type) => (
        type.serviceCategoryId === this.serviceCategory
          || !this.serviceCategory
      ));
    },
    suggestedModelNumbers() {
      return [...this.options, ...this.predictedOptions];
    },
    modelManufacturer() {
      return {
        manufacturer: this.manufacturer,
        assetType: this.assetType,
      };
    },
    missingInfo() {
      const missing = [];
      if (!this.manufacturer) missing.push('Manufacturer');
      if (!this.modelNumber) missing.push('Model #');
      if (!this.serialNumber) missing.push('Serial #');
      return missing.join(', ');
    },
    selectedLocation() {
      return this.locations.find((location) => location.id === this.location);
    },
    availableCreateReasons() {
      if (this.selectedLocation?.status == "Onboarding") {
        return [
          "Onboarding"
        ]
      }
      const baseCreateReasons = ['Existing Equipment','Acquired New Equipment'];
      if (this.isSsp){
        return [
          ...baseCreateReasons,
          'Moving Existing Equipment to New Location'
        ]
      }
      return baseCreateReasons;
    },
    accountManagerEmailLink() {
      return `mailto:${this.accountManagerEmail}`;
    },
  },
  mounted() {
    this.$nextTick(() => this.$refs.basicInfoForm.resetValidation());
  },
  watch: {
    modelManufacturer: {
      handler(val) {
        if (val.assetType?.id != null && val.manufacturer != null) {
          this.getModelNumberSuggestions(val.assetType.id, val.manufacturer);
        } else {
          this.predictedOptions = [];
        }
      },
      deep: true,
    },
    assetType: {
      handler() {
        if (this.assetType?.serviceCategoryId) {
          this.serviceCategory = this.$set(this.assetType, 'serviceCategoryId', this.assetType.serviceCategoryId);
        }
      },
      deep: true,
    },
    selectedLocationId: {
      handler(val) {
        this.$set(this, 'location', val);
      },
      immediate: true,
    },
  },
  methods: {
    setManufacturer(item) {
      this.manufacturer = item.id || item;
    },
    selectAssetType(type) {
      if (type?.serviceCategoryId !== this.serviceCategory) {
        this.serviceCategory = type?.serviceCategoryId;
      }
    },
    close() {
      this.$emit('closeNewAssetDialog');
    },
    async fileUpload(file, type) {
      const url = await this.getFileURL(file);
      this.photos.push({
        photoUrl: url,
        file,
        photoType: type,
      });
    },
    async getFileURL(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          resolve(e.target.result);
        };
        reader.onerror = (err) => {
          reject(err);
        };
        reader.readAsDataURL(file);
      });
    },
    async createAsset() {
      this.creatingAsset = true;
      const asset = {
        locationId: this.location,
        assetTypeId: this.assetType.id,
        description: this.description,
      };
      if (this.manufacturer) {
        asset.manufacturerId = this.manufacturer;
      }
      if (this.modelNumber) {
        asset.modelNumber = this.modelNumber;
      }
      if (this.serialNumber) {
        asset.serialNumber = this.serialNumber;
      }
      this.$emit('addAsset', {
        asset,
        photos: this.photos,
        done: this.doneCallback,
      });
    },
    getModelNumberSuggestions(assetTypeId, manufacturerId) {
      this.getAssetModelNumbers({
        manufacturerId: manufacturerId ?? undefined,
        assetTypeId: assetTypeId ?? undefined,
      }).then((response) => {
        this.predictedOptions = response;
      });
    },
    doneCallback() {
      this.creatingAsset = false;
      this.newAssetStep = 3;
    },
    resetComponent() {
      Object.assign(this.$data, initialData());
    },
  },
};
</script>
